import { getPlatformUrlParts } from '@acl-services/highbond-common';
import { ObjectTypes } from '@diligentcorp/rc-service-object-mapping';

export function getRelationshipLinkBaseUrl(): string {
  if (
    typeof window === 'undefined' ||
    window.location.hostname === 'localhost'
  ) {
    return '';
  }
  return window.location.origin;
}

type GetRelationshipObjectUrlParams = {
  objectType: string;
  objectId: string;
  baseUrl: string;
  hroid?: string;
  service?: string;
};

/**
 * Object types that do not support relationship links (e.g. no navigable URL).
 * For these types, the relationship ID is shown as plain text only.
 * For Evidence we would need the assessment id as well to build the URL.
 * For Asset we would need the type as well to build the URL.
 */
const OBJECT_TYPES_WITHOUT_LINKS: Set<string> = new Set([
  ObjectTypes.EVIDENCE,
  ObjectTypes.ASSET,
]);

const TYPE_TO_APP_AND_PATH: Partial<
  Record<string, { app: string; path: string }>
> = {
  [ObjectTypes.PROCESS]: { app: 'risk-manager', path: 'processes' },
  [ObjectTypes.OBJECTIVE]: { app: 'risk-manager', path: 'objectives' },
  [ObjectTypes.RISK]: { app: 'risk-manager', path: 'risks' },
  [ObjectTypes.RISK_MITIGATION]: {
    app: 'risk-manager',
    path: 'records/risk_mitigation',
  },
  [ObjectTypes.RISK_EVENT_ASSESSMENT]: {
    app: 'risk-manager',
    path: 'records/risk_event_assessment',
  },
  'risk-assessment': {
    app: 'risk-manager',
    path: 'records/risk_event_assessment',
  },
  [ObjectTypes.AUDIT]: { app: 'audit', path: 'audit-fieldwork/audits' },
  [ObjectTypes.FINDING]: { app: 'asset-manager', path: 'records/finding' },
};

function buildBaseUrl(baseUrl: string, app: string): string {
  const { protocol, orgSubdomain, region, domain, domainSuffix, tld } =
    getPlatformUrlParts(baseUrl);
  const host = `${domain}${domainSuffix}.${tld}`;
  const regionSuffix = region === 'us' || !region ? '' : `-${region}`;
  return `${protocol}://${orgSubdomain}.${app}${regionSuffix}.${host}`;
}

function getControlUrl(baseUrl: string, objectId: string): string | null {
  const base = buildBaseUrl(baseUrl, 'risk-manager');
  return `${base}/controls/${encodeURIComponent(objectId)}/details`;
}

function getControlAssessmentUrl(
  baseUrl: string,
  objectId: string,
  service?: string,
): string | null {
  if (service === 'internal-controls') {
    const base = buildBaseUrl(baseUrl, 'internal-controls');
    return `${base}/control-assessments/${encodeURIComponent(
      objectId,
    )}/findings`;
  }
  if (service === 'risk-manager') {
    const base = buildBaseUrl(baseUrl, 'risk-manager');
    return `${base}/records/control_assessments/${encodeURIComponent(
      objectId,
    )}/details`;
  }
  return null;
}

export function getRelationshipObjectUrl(
  params: GetRelationshipObjectUrlParams,
): string | null {
  const { objectType, objectId, baseUrl, hroid, service } = params;
  if (!baseUrl) return null;
  if (OBJECT_TYPES_WITHOUT_LINKS.has(objectType)) return null;

  if (objectType === ObjectTypes.CONTROL) {
    return getControlUrl(baseUrl, objectId);
  }

  if (objectType === ObjectTypes.CONTROL_ASSESSMENT) {
    return getControlAssessmentUrl(baseUrl, objectId, service);
  }

  const entry = TYPE_TO_APP_AND_PATH[objectType];
  if (!entry) return null;

  if (objectType === ObjectTypes.AUDIT && !hroid) return null;

  const base = buildBaseUrl(baseUrl, entry.app);

  switch (objectType) {
    case ObjectTypes.AUDIT:
      return `${base}/${entry.path}/${encodeURIComponent(hroid!)}/results`;
    default:
      return `${base}/${entry.path}/${encodeURIComponent(objectId)}`;
  }
}
